import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import { WEBSITE_SERVICE_INTEREST_OPTIONS } from '../../constants/websiteServiceInterests';
import { sendContactFormEmail, isEmailJsConfigured } from '../../utils/emailjsContact';
import toast from 'react-hot-toast';
import contactHeroImage from '../../images/herosection/heromain.jpg';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!isEmailJsConfigured()) {
        toast.error(
          'Contact email is not configured. Add EmailJS keys to your .env file (see .env.example) and restart the dev server.'
        );
        return;
      }

      await sendContactFormEmail({ ...formData });

      toast.success('Thank you for your inquiry! We will contact you soon.');
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      const msg =
        error?.message ||
        error?.text ||
        'Failed to send your message. Please try again or call us directly.';
      toast.error(typeof msg === 'string' ? msg : 'Failed to send your message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Mobile',
      value: '+91 8329392431',
      tel: 'tel:+918329392431',
      description: 'Call or WhatsApp'
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Office phone',
      value: '022-44505126',
      tel: 'tel:+912244505126',
      description: 'Landline'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      value: 'kirbooindia@gmail.com',
      mailto: 'kirbooindia@gmail.com',
      description: 'Primary inbox — we respond within 24 hours'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Corporate email',
      value: 'contact@kirbooflowsafe.com',
      mailto: 'contact@kirbooflowsafe.com',
      description: 'Official company email'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Registered Office',
      value: 'C26/02, Panchatara, Sector 15, Airoli, Navi Mumbai – 400708',
      description: 'Corporate Address'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Works Address',
      value: 'Gat No. 265/1, Shinganapur road, Natepute, Tal – Malshiras, Dist – Solapur - 413109',
      description: 'Manufacturing Facility'
    },
    // {
    //   icon: <Clock className="w-6 h-6" />,
    //   title: 'Business Hours',
    //   value: 'Monday - Friday: 8:00 AM - 6:00 PM',
    //   description: 'Emergency support available 24/7'
    // }
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section 
        className="contact-hero"
        style={{
          backgroundImage: `url(${contactHeroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="contact-hero-overlay"></div>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="contact-hero-content"
          >
            <Breadcrumb />
            <h1 className="contact-hero-title">
              Get in <span className="contact-hero-title-highlight">Touch</span>
            </h1>
            <p className="contact-hero-description">
              Ready to optimize your flow control systems? Contact us for expert consultation, 
              free quotes, and 24/7 support for all your valve and actuator needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="contact-main">
        <div className="container-custom">
          <div className="contact-grid">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="contact-form-section"
            >
              <h2 className="contact-form-title">Send us a Message</h2>
              <p className="contact-form-description">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="contact-success"
                >
                  <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
                  <h3 className="contact-success-title">Message Sent Successfully!</h3>
                  <p className="contact-success-description">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="contact-form-grid">
                    <div className="contact-form-group">
                      <label htmlFor="name" className="contact-form-label">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="contact-form-input"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="contact-form-group">
                      <label htmlFor="email" className="contact-form-label">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="contact-form-input"
                        placeholder="Enter your email address"
                      />
                    </div>

                    <div className="contact-form-group">
                      <label htmlFor="phone" className="contact-form-label">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="contact-form-input"
                        placeholder="Enter your phone number"
                      />
                    </div>

                    <div className="contact-form-group">
                      <label htmlFor="company" className="contact-form-label">Company Name</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="contact-form-input"
                        placeholder="Enter your company name"
                      />
                    </div>

                    <div className="contact-form-group contact-form-full-width">
                      <label htmlFor="service" className="contact-form-label">Service Required</label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="contact-form-select"
                      >
                        <option value="">Select a service</option>
                        {WEBSITE_SERVICE_INTEREST_OPTIONS.map((service) => (
                          <option key={service} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="contact-form-group contact-form-full-width">
                      <label htmlFor="message" className="contact-form-label">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows="5"
                        className="contact-form-textarea"
                        placeholder="Tell us about your requirements..."
                      />
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="contact-form-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="contact-info-section"
            >
              <h2 className="contact-info-title">Contact Information</h2>
              <p className="contact-info-description">
                Get in touch with us through any of these channels
              </p>

              <div className="contact-info-grid">
                {contactInfo.map((info, index) => (
                  <div key={index} className="contact-info-item">
                    <div className="contact-info-icon">
                      <div className="contact-info-icon-wrapper">
                        {info.icon}
                      </div>
                    </div>
                    <div className="contact-info-content">
                      <h3 className="contact-info-item-title">{info.title}</h3>
                      {info.tel ? (
                        <a href={info.tel} className="contact-info-item-value block hover:underline text-primary-600">
                          {info.value}
                        </a>
                      ) : info.mailto ? (
                        <a
                          href={`mailto:${info.mailto}`}
                          className="contact-info-item-value block hover:underline text-primary-600"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="contact-info-item-value">{info.value}</p>
                      )}
                      <p className="contact-info-item-description">{info.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* <div className="contact-emergency">
                <h3 className="contact-emergency-title">Emergency Support</h3>
                <p className="contact-emergency-description">
                  For critical system failures and emergency repairs, call our 24/7 hotline
                </p>
                <a href="tel:+912244505126" className="contact-emergency-button">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Emergency Hotline
                </a>
              </div> */}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="contact-map">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="contact-map-content"
          >
            <h2 className="contact-map-title">Find Us</h2>
            <p className="contact-map-description">
              Visit our facility or contact us for on-site services
            </p>
            
            <div className="contact-map-grid">
              <div className="contact-map-item">
                <h3 className="contact-map-location-title">Registered Office</h3>
                <p className="contact-map-location-address">
                  C26/02, Panchatara, Sector 15, Airoli, Navi Mumbai – 400708
                </p>
                <div className="contact-map-container">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.2345678901234!2d73.0238!3d19.1596!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c9c676018b43%3A0x1f47a7c1a8a9a9a9!2sAiroli%2C%20Navi%20Mumbai%2C%20Maharashtra%20400708!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Registered Office Location"
                  ></iframe>
                </div>
              </div>
              
              <div className="contact-map-item">
                <h3 className="contact-map-location-title">Works Address</h3>
                <p className="contact-map-location-address">
                  Gat No. 265/1, Shinganapur road, Natepute, Tal – Malshiras, Dist – Solapur - 413109
                </p>
                <div className="contact-map-container">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.2345678901234!2d75.1234!3d17.5678!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2c9c676018b43%3A0x1f47a7c1a8a9a9a9!2sSolapur%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Works Address Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;

